import * as Header from '-/Custom/Header';
import * as Clipboard from 'expo-clipboard';
import { Image } from 'expo-image';
import { useLocalSearchParams, useRouter } from 'expo-router';
import * as Sharing from 'expo-sharing';
import { StatusBar } from 'expo-status-bar';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import Animated, {
  runOnJS,
  SensorType,
  useAnimatedReaction,
  useAnimatedSensor,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';
import ZoomableImage from '@/components/Custom/ZoomableImage';
import { createTapCoordinator } from '@/components/Custom/ZoomableImageTapCoordinator';
import Box from '@/components/UI/Box';
import LoadingPlaceholder from '@/components/UI/LoadingPlaceholder';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import useIsSharingAvailable from '@/hooks/useIsSharingAvailable';
import useTimer from '@/hooks/useTimer';
import useThemeStore from '@/stores/useThemeStore';
import hapticsOnPress from '@/utils/hapticsOnPress';
import { toggleImageHeaderVisibility } from './ChatImageHeaderVisibility';
import {
  type ChatImageTiltRotation,
  getNextChatImageTiltRotation,
  getRotatedChatImageSize,
} from './ChatImageTiltRotation';

const TILT_ROTATION_DURATION_MS = 250;
const SUPPORTS_TILT_ROTATION = Platform.OS !== 'web';

export default function ChatImageDetail() {
  const [isLoading, setIsLoading] = useState(true);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [imageSize, setImageSize] = useState<
    { width: number; height: number } | undefined
  >();
  const { url } = useLocalSearchParams();
  const { back } = useRouter();
  const isSharingAvailable = useIsSharingAvailable();
  const [copyConfirmText, triggerCopyConfirmText] = useTimer('');
  const realTheme = useThemeStore((state) => state.realTheme);
  const { width: viewportWidth, height: viewportHeight } =
    useWindowDimensions();
  const [tiltRotation, setTiltRotation] = useState<ChatImageTiltRotation>(0);
  const targetTiltRotation = useSharedValue<ChatImageTiltRotation>(0);
  const animatedTiltRotation = useSharedValue(0);
  const gravity = useAnimatedSensor(SensorType.GRAVITY, {
    adjustToInterfaceOrientation: false,
    interval: 100,
  });

  useAnimatedReaction(
    () => {
      if (!SUPPORTS_TILT_ROTATION) {
        return 0;
      }

      return getNextChatImageTiltRotation(
        targetTiltRotation.value,
        gravity.sensor.value,
      );
    },
    (nextRotation) => {
      if (nextRotation === targetTiltRotation.value) {
        return;
      }

      targetTiltRotation.value = nextRotation;
      animatedTiltRotation.value = withTiming(nextRotation, {
        duration: TILT_ROTATION_DURATION_MS,
      });
      runOnJS(setTiltRotation)(nextRotation);
    },
  );

  const viewerCanvasStyle = useAnimatedStyle(() => {
    const isLandscape = targetTiltRotation.value !== 0;
    const canvasWidth = isLandscape ? viewportHeight : viewportWidth;
    const canvasHeight = isLandscape ? viewportWidth : viewportHeight;

    return {
      width: canvasWidth,
      height: canvasHeight,
      left: (viewportWidth - canvasWidth) / 2,
      top: (viewportHeight - canvasHeight) / 2,
      transform: [{ rotateZ: `${animatedTiltRotation.value}deg` }],
    };
  });

  const isTiltedLandscape = SUPPORTS_TILT_ROTATION && tiltRotation !== 0;
  const rotatedImageSize = isTiltedLandscape
    ? getRotatedChatImageSize(tiltRotation, imageSize)
    : imageSize;
  const viewerCanvas = SUPPORTS_TILT_ROTATION
    ? viewerCanvasStyle
    : styles.webViewerCanvas;

  const hideHeader = useCallback(() => {
    setIsHeaderVisible(false);
  }, []);
  const showHeader = useCallback(() => {
    setIsHeaderVisible(true);
  }, []);
  const headerTapCoordinator = useMemo(
    () =>
      createTapCoordinator({
        onDoubleTap: hideHeader,
        onSingleTap: () => {
          setIsHeaderVisible(toggleImageHeaderVisibility);
        },
      }),
    [hideHeader],
  );
  const handleHeaderTap = useCallback(() => {
    headerTapCoordinator.singleTap();
  }, [headerTapCoordinator]);
  const cancelHeaderTap = useCallback(() => {
    headerTapCoordinator.cancel();
    hideHeader();
  }, [headerTapCoordinator, hideHeader]);

  useEffect(() => {
    return () => {
      headerTapCoordinator.cancel();
    };
  }, [headerTapCoordinator]);

  const onPressShare = async () => {
    if (url && typeof url === 'string') {
      await Sharing.shareAsync(url);
    }
  };
  const headerActions = (
    <Header.ButtonGroup>
      <Box direction="horizontal" space="xs" align="center">
        {copyConfirmText ? <Text>{copyConfirmText}</Text> : null}
        <Header.Button
          accessibilityRole="button"
          accessibilityLabel="Copy image link"
          onPress={hapticsOnPress(async () => {
            if (url && typeof url === 'string') {
              await Clipboard.setStringAsync(url);
              triggerCopyConfirmText('Copied Image Link.');
            }
          })}
        >
          <Icon name="copy-outline" />
        </Header.Button>
        {isSharingAvailable ? (
          <Header.Button
            accessibilityRole="button"
            accessibilityLabel="Share image"
            onPress={hapticsOnPress(onPressShare)}
          >
            <Icon name="share-outline" />
          </Header.Button>
        ) : null}
      </Box>
    </Header.ButtonGroup>
  );
  return (
    <>
      <StatusBar
        hidden={isTiltedLandscape || !isHeaderVisible}
        style={realTheme === 'dark' ? 'light' : 'dark'}
        animated
      />
      {isLoading ? (
        <Box
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          zIndex={1}
          center
        >
          <LoadingPlaceholder />
        </Box>
      ) : null}
      <Box
        style={styles.viewerBackground}
        flex
        justify="center"
        align="center"
        overflow="hidden"
      >
        <ZoomableImage
          imageSize={rotatedImageSize}
          maxScale={8}
          doubleTapScale={3}
          singleClickZoom={Platform.OS === 'web'}
          onTap={handleHeaderTap}
          onDoubleTap={headerTapCoordinator.doubleTap}
          onPanStart={cancelHeaderTap}
          onPinchStart={cancelHeaderTap}
          onReturnToMinimumScale={showHeader}
        >
          <Animated.View style={[styles.viewerCanvas, viewerCanvas]}>
            <Image
              contentFit="contain"
              style={{ width: '100%', height: '100%' }}
              source={url}
              onLoad={(event) => {
                setImageSize({
                  width: event.source.width,
                  height: event.source.height,
                });
                setIsLoading(false);
              }}
            />
          </Animated.View>
        </ZoomableImage>
        {isHeaderVisible ? (
          <Animated.View
            style={[styles.viewerCanvas, viewerCanvas]}
            pointerEvents="box-none"
          >
            <Box
              testID="image-header-overlay"
              style={styles.headerOverlay(isTiltedLandscape)}
              position="absolute"
              top={0}
              left={0}
              right={0}
              zIndex={2}
              pointerEvents="box-none"
            >
              <Box
                style={styles.headerToolbar(isTiltedLandscape)}
                direction="horizontal"
                align="center"
                height={56}
                pointerEvents="box-none"
              >
                <Box
                  direction="horizontal"
                  align="center"
                  justify="space-between"
                  flex
                  px="md"
                  pointerEvents="box-none"
                >
                  <Header.Button
                    accessibilityRole="button"
                    accessibilityLabel="Close"
                    onPress={back}
                    hitSlop={12}
                  >
                    <Icon name="close-outline" size="3xl" />
                  </Header.Button>
                  {headerActions}
                </Box>
              </Box>
            </Box>
          </Animated.View>
        ) : null}
      </Box>
    </>
  );
}

const styles = StyleSheet.create((theme, runtime) => ({
  viewerBackground: {
    backgroundColor: theme.colors.background,
  },
  viewerCanvas: {
    position: 'absolute',
  },
  webViewerCanvas: {
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
  },
  headerOverlay: (isTiltedLandscape: boolean) => ({
    backgroundColor: theme.colors.background,
    paddingTop: isTiltedLandscape ? 0 : runtime.insets.top,
  }),
  headerToolbar: (isTiltedLandscape: boolean) => {
    const landscapeHorizontalInset = Math.max(
      runtime.insets.top,
      runtime.insets.bottom,
    );

    return {
      paddingLeft: isTiltedLandscape
        ? landscapeHorizontalInset
        : runtime.insets.left,
      paddingRight: isTiltedLandscape
        ? landscapeHorizontalInset
        : runtime.insets.right,
    };
  },
}));
